import { useQuery } from '@tanstack/react-query';
import { partApi } from '../api/partApi';
import type { PartFilters } from '../types/part.types';

/**
 * Hooks React Query pour la gestion des pièces automobiles
 */

// ==================== QUERIES (lecture) ====================

/**
 * Liste des pièces avec filtres
 */
export const useParts = (filters?: PartFilters) => {
  return useQuery({
    queryKey: ['parts', filters],
    queryFn: () => partApi.getParts(filters),
    staleTime: 300000, // 5 minutes (données quasi-statiques)
  });
};

/**
 * Détail d'une pièce
 */
export const usePart = (id: number | undefined) => {
  return useQuery({
    queryKey: ['part', id],
    queryFn: () => partApi.getPart(id!),
    enabled: !!id,
    staleTime: 300000,
  });
};

/**
 * Arbre hiérarchique des pièces (catégories + sous-catégories + pièces)
 */
export const usePartsTree = (locale = 'fr', vehicleType?: number) => {
  return useQuery({
    queryKey: ['parts-tree', locale, vehicleType],
    queryFn: () => partApi.getPartsTree(locale, vehicleType),
    staleTime: 300000,
  });
};

/**
 * Recherche de pièces par texte
 */
export const useSearchParts = (query: string, locale = 'fr', enabled = true) => {
  return useQuery({
    queryKey: ['parts-search', query, locale],
    queryFn: () => partApi.searchParts(query, locale),
    enabled: enabled && query.length >= 2, // Minimum 2 caractères
    staleTime: 60000, // 1 minute
  });
};

/**
 * Liste de toutes les catégories
 */
export const useCategories = (locale = 'fr') => {
  return useQuery({
    queryKey: ['categories', locale],
    queryFn: () => partApi.getCategories(locale),
    staleTime: 300000,
  });
};

/**
 * Détail d'une catégorie
 */
export const useCategory = (id: number | undefined, locale = 'fr') => {
  return useQuery({
    queryKey: ['category', id, locale],
    queryFn: () => partApi.getCategory(id!, locale),
    enabled: !!id,
    staleTime: 300000,
  });
};

/**
 * Catégories racines (top-level)
 */
export const useRootCategories = (locale = 'fr') => {
  return useQuery({
    queryKey: ['root-categories', locale],
    queryFn: () => partApi.getRootCategories(locale),
    staleTime: 300000,
  });
};

/**
 * Sous-catégories d'une catégorie
 */
export const useSubcategories = (categoryId: number | undefined, locale = 'fr') => {
  return useQuery({
    queryKey: ['subcategories', categoryId, locale],
    queryFn: () => partApi.getSubcategories(categoryId!, locale),
    enabled: !!categoryId,
    staleTime: 300000,
  });
};

/**
 * Pièces d'une catégorie
 */
export const usePartsByCategory = (categoryId: number | undefined, locale = 'fr') => {
  return useQuery({
    queryKey: ['parts-by-category', categoryId, locale],
    queryFn: () => partApi.getPartsByCategory(categoryId!, locale),
    enabled: !!categoryId,
    staleTime: 300000,
  });
};

/**
 * Pièces recommandées pour un type de garantie
 */
export const useRecommendedParts = (
  warrantyType: number | undefined,
  vehicleType: number | undefined,
  locale = 'fr',
) => {
  return useQuery({
    queryKey: ['recommended-parts', warrantyType, vehicleType, locale],
    queryFn: () => partApi.getRecommendedParts(warrantyType!, vehicleType!, locale),
    enabled: warrantyType !== undefined && vehicleType !== undefined,
    staleTime: 300000,
  });
};

/**
 * Pièces essentielles (priorité 1)
 */
export const useEssentialParts = (locale = 'fr') => {
  return useQuery({
    queryKey: ['essential-parts', locale],
    queryFn: () => partApi.getEssentialParts(locale),
    staleTime: 300000,
  });
};

/**
 * Liste complète de toutes les pièces (pour affichage dans le récapitulatif)
 */
export const usePartsList = (locale = 'fr', vehicleType?: number) => {
  return useQuery({
    queryKey: ['parts-list', locale, vehicleType],
    queryFn: async () => {
      const result = await partApi.getParts({ locale });
      return result.parts;
    },
    staleTime: 300000,
    enabled: vehicleType !== undefined,
  });
};
